import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AddOutlined from '@mui/icons-material/AddOutlined';
import PersonRemoveOutlined from '@mui/icons-material/PersonRemoveOutlined';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import BookmarkBorderOutlined from '@mui/icons-material/BookmarkBorderOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import API_BASE from '../config/api';

const TABS = ['Overview', 'Team', 'Tasks & Notes', 'Vendors', 'Venue', 'Directions', 'Memory Bank', 'Documents', 'Activity'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const STATUS_OPTIONS = ['Proposal', 'Confirmed', 'Active', 'Completed', 'Archived'];
const STATUS_COLORS = {
  'Proposal':  { bg: 'rgba(229,192,75,0.1)',  color: '#E5C04B' },
  'Confirmed': { bg: 'rgba(83,145,255,0.1)',  color: '#5391FF' },
  'Active':    { bg: 'rgba(57,255,20,0.1)',   color: '#39FF14' },
  'Completed': { bg: 'rgba(86,86,86,0.2)',    color: '#565656' },
  'Archived':  { bg: 'rgba(40,40,40,0.8)',    color: '#565656' },
};

const VENDOR_TYPES = [
  'AVL Production', 'Staging', 'Labor', 'Trucking', 'Power',
  'Rigging', 'Scenic', 'Security', 'Hospitality', 'Transportation', 'Other',
];

function toDateInput(d) {
  if (!d) return '';
  const date = new Date(d);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function formatDateRange(start, end) {
  if (!start && !end) return '';
  const opts = { month: 'short', day: 'numeric' };
  const yearOpts = { month: 'short', day: 'numeric', year: 'numeric' };
  const fmt = (d, o) => new Date(d).toLocaleDateString('en-US', o);
  if (start && end) {
    const s = new Date(start);
    const e = new Date(end);
    if (s.getFullYear() === e.getFullYear()) {
      return `${fmt(s, opts)} — ${fmt(e, yearOpts)}`;
    }
    return `${fmt(s, yearOpts)} — ${fmt(e, yearOpts)}`;
  }
  if (start) return fmt(start, yearOpts);
  return fmt(end, yearOpts);
}

function formatTimestamp(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function initFormData(ev) {
  return {
    title: ev.title || '',
    status: ev.status || 'Proposal',
    start_date: toDateInput(ev.start_date),
    end_date: toDateInput(ev.end_date),
    location: ev.location || '',
    event_city: ev.event_city || '',
    event_state: ev.event_state || '',
    tickets_sold: ev.tickets_sold != null ? String(ev.tickets_sold) : '',
    tickets_on_sale: ev.tickets_on_sale != null ? String(ev.tickets_on_sale) : '',
    notes: ev.notes || '',
    parking_notes: ev.parking_notes || '',
    vc_wifi_name: ev.venue_context?.show_wifi_name || '',
    vc_wifi_password: ev.venue_context?.show_wifi_password || '',
    vc_load_in_notes: ev.venue_context?.show_load_in_notes || '',
    vc_access_notes: ev.venue_context?.show_access_notes || '',
    vc_network_notes: ev.venue_context?.show_network_notes || '',
  };
}

function TaskCheckbox({ checked, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '18px', height: '18px', minWidth: '18px',
        borderRadius: '3px',
        border: checked ? 'none' : '2px solid #565656',
        backgroundColor: checked ? '#39FF14' : 'transparent',
        cursor: 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'center', transition: 'all 0.15s ease',
      }}
    >
      {checked && (
        <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
          <path d="M1 4L4 7L10 1" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

export default function EventDetail({ event, onUpdate, workspaceId }) {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(() => initFormData(event));
  const formDataRef = useRef(formData);
  const [localEvent, setLocalEvent] = useState(event);
  const [savedFields, setSavedFields] = useState({});
  const [errorFields, setErrorFields] = useState({});

  // Project link
  const [linkedProject, setLinkedProject] = useState(null);

  // Full venue record (for Venue tab)
  const [fullVenueRecord, setFullVenueRecord] = useState(null);
  const [isLoadingVenue, setIsLoadingVenue] = useState(false);

  // Team
  const [eventTeam, setEventTeam] = useState([]);
  const [memberPositions, setMemberPositions] = useState({});
  const [memberSearch, setMemberSearch] = useState('');
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [availablePersonnel, setAvailablePersonnel] = useState([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const memberPickerRef = useRef(null);

  // Show Manager
  const [linkedShowManager, setLinkedShowManager] = useState(null);
  const [showManagerPicker, setShowManagerPicker] = useState(false);
  const [showManagerSearch, setShowManagerSearch] = useState('');
  const showManagerPickerRef = useRef(null);

  // Venue
  const [linkedVenue, setLinkedVenue] = useState(null);
  const [showVenuePicker, setShowVenuePicker] = useState(false);
  const [venueSearch, setVenueSearch] = useState('');
  const [availableVenues, setAvailableVenues] = useState([]);
  const venuePickerRef = useRef(null);

  // Tasks
  const [newTaskText, setNewTaskText] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [taskAssignees, setTaskAssignees] = useState({});
  const [activeTaskPicker, setActiveTaskPicker] = useState(null);
  const [taskSearch, setTaskSearch] = useState('');
  const taskPickerRef = useRef(null);

  // Vendors
  const [availableVendors, setAvailableVendors] = useState([]);
  const [eventVendors, setEventVendors] = useState([]);
  const [showVendorPicker, setShowVendorPicker] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [pendingVendorType, setPendingVendorType] = useState('');
  const vendorPickerRef = useRef(null);

  // Rep assignment
  const [companyContacts, setCompanyContacts] = useState({});
  const [activeRepPicker, setActiveRepPicker] = useState(null);
  const [repPickerSearch, setRepPickerSearch] = useState('');
  const repPickerRef = useRef(null);

  // Directions
  const [showAddDirectionForm, setShowAddDirectionForm] = useState(false);
  const [newDirectionTitle, setNewDirectionTitle] = useState('');
  const [newDirectionContent, setNewDirectionContent] = useState('');
  const [isSavingDirection, setIsSavingDirection] = useState(false);

  // Memory Bank
  const [memoryBankText, setMemoryBankText] = useState('');
  const [isAddingMemory, setIsAddingMemory] = useState(false);
  const [memoryBankHover, setMemoryBankHover] = useState(false);

  // ─── sync formDataRef ───
  useEffect(() => { formDataRef.current = formData; }, [formData]);

  // ─── reset on event change ───
  useEffect(() => {
    setFormData(initFormData(event));
    setLocalEvent(event);
    setActiveTab(0);
    setSavedFields({});
    setErrorFields({});
    setNewTaskText('');
    setTaskAssignees({});
    setActiveTaskPicker(null);
    setLinkedShowManager(null);
    setShowManagerPicker(false);
    setShowManagerSearch('');
    setLinkedVenue(null);
    setShowVenuePicker(false);
    setVenueSearch('');
    setEventTeam([]);
    setMemberPositions({});
    setShowMemberPicker(false);
    setEventVendors([]);
    setShowVendorPicker(false);
    setVendorSearch('');
    setMemoryBankText('');
    setShowAddDirectionForm(false);
    setLinkedProject(null);
    setFullVenueRecord(null);

    const posMap = {};
    (event.team_members || []).forEach((m) => {
      if (m.personnel_profile_id) posMap[m.personnel_profile_id.toString()] = m.position || '';
    });
    setMemberPositions(posMap);

    if (event.project_id) {
      axios.get(`${API_BASE}/api/projects/${event.project_id}`,
        { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setLinkedProject(r.data)).catch(() => {});
    }

    if (event.venue_id) {
      setIsLoadingVenue(true);
      axios.get(`${API_BASE}/api/venues/${event.venue_id}`,
        { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { setLinkedVenue(r.data); setFullVenueRecord(r.data); setIsLoadingVenue(false); })
        .catch(() => setIsLoadingVenue(false));
    }

    if (event.team_members && event.team_members.length > 0) {
      setIsLoadingTeam(true);
      Promise.all(
        event.team_members.map((m) =>
          axios.get(`${API_BASE}/api/personnel/${m.personnel_profile_id}`, headers)
            .then((r) => r.data).catch(() => null)
        )
      ).then((profiles) => {
        const loaded = profiles.filter(Boolean);
        setEventTeam(loaded);
        setIsLoadingTeam(false);
        const assigneeMap = {};
        (event.tasks || []).forEach((task) => {
          if (task.assignee_id) {
            const p = loaded.find((pr) => pr._id?.toString() === task.assignee_id?.toString());
            if (p) assigneeMap[task._id] = p;
          }
        });
        setTaskAssignees(assigneeMap);
      });
    }

    if (event.vendor_assignments && event.vendor_assignments.length > 0) {
      Promise.all(
        event.vendor_assignments.map((va) =>
          axios.get(`${API_BASE}/api/companies/${va.company_id}`, headers)
            .then((r) => ({ ...va, company: r.data })).catch(() => null)
        )
      ).then((results) => setEventVendors(results.filter(Boolean)));
    }
  }, [event._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── fetch global lists ───
  useEffect(() => {
    if (!token) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API_BASE}/api/personnel`, authHeaders)
      .then((r) => setAvailablePersonnel(r.data)).catch(() => {});
    axios.get(`${API_BASE}/api/venues`, authHeaders)
      .then((r) => setAvailableVenues(r.data)).catch(() => {});
    if (workspaceId) {
      axios.get(`${API_BASE}/api/companies`, {
        params: { workspace_id: workspaceId, type: 'vendor' },
        ...authHeaders,
      }).then((r) => setAvailableVendors(r.data)).catch(() => {});
    }
  }, [workspaceId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── fetch fullVenueRecord when linkedVenue changes ───
  useEffect(() => {
    if (!linkedVenue?._id) { setFullVenueRecord(null); return; }
    if (fullVenueRecord?._id?.toString() === linkedVenue._id?.toString()) return;
    setIsLoadingVenue(true);
    axios.get(`${API_BASE}/api/venues/${linkedVenue._id}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setFullVenueRecord(r.data); setIsLoadingVenue(false); })
      .catch(() => setIsLoadingVenue(false));
  }, [linkedVenue?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── resolve show manager from personnel list ───
  useEffect(() => {
    if (localEvent.show_manager_id && availablePersonnel.length > 0) {
      const found = availablePersonnel.find(
        (p) => p._id?.toString() === localEvent.show_manager_id?.toString()
      );
      setLinkedShowManager(found || null);
    } else if (!localEvent.show_manager_id) {
      setLinkedShowManager(null);
    }
  }, [localEvent.show_manager_id, availablePersonnel.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── outside click ───
  useEffect(() => {
    function handleOutside(e) {
      if (memberPickerRef.current && !memberPickerRef.current.contains(e.target)) setShowMemberPicker(false);
      if (showManagerPickerRef.current && !showManagerPickerRef.current.contains(e.target)) setShowManagerPicker(false);
      if (venuePickerRef.current && !venuePickerRef.current.contains(e.target)) setShowVenuePicker(false);
      if (vendorPickerRef.current && !vendorPickerRef.current.contains(e.target)) setShowVendorPicker(false);
      if (taskPickerRef.current && !taskPickerRef.current.contains(e.target)) setActiveTaskPicker(null);
      if (repPickerRef.current && !repPickerRef.current.contains(e.target)) setActiveRepPicker(null);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // ─── helpers ───
  function showSaved(field) {
    setSavedFields((p) => ({ ...p, [field]: true }));
    setTimeout(() => setSavedFields((p) => ({ ...p, [field]: false })), 2000);
  }
  function showError(field) {
    setErrorFields((p) => ({ ...p, [field]: true }));
    setTimeout(() => setErrorFields((p) => ({ ...p, [field]: false })), 3000);
  }

  async function handleBlur(field) {
    const value = formDataRef.current[field];
    let payload;
    if (field === 'start_date' || field === 'end_date') {
      payload = { [field]: value || null };
    } else if (field === 'tickets_sold') {
      payload = { tickets_sold: value === '' ? null : Number(value) };
    } else if (field === 'tickets_on_sale') {
      payload = { tickets_on_sale: value === '' ? null : Number(value) };
    } else if (field === 'vc_wifi_name') {
      payload = { venue_context: { show_wifi_name: value } };
    } else if (field === 'vc_wifi_password') {
      payload = { venue_context: { show_wifi_password: value } };
    } else if (field === 'vc_load_in_notes') {
      payload = { venue_context: { show_load_in_notes: value } };
    } else if (field === 'vc_access_notes') {
      payload = { venue_context: { show_access_notes: value } };
    } else if (field === 'vc_network_notes') {
      payload = { venue_context: { show_network_notes: value } };
    } else {
      payload = { [field]: value };
    }
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`, payload, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      showSaved(field);
    } catch {
      showError(field);
    }
  }

  async function handleStatusChange(val) {
    setFormData((p) => ({ ...p, status: val }));
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`, { status: val }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      showSaved('status');
    } catch { showError('status'); }
  }

  // ─── venue handlers ───
  async function handleSelectVenue(venue) {
    const newLocation = [venue.city, venue.state].filter(Boolean).join(', ');
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        {
          venue_id: venue._id,
          location: newLocation || formDataRef.current.location,
          event_city: venue.city || null,
          event_state: venue.state || null,
        }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setLinkedVenue(venue);
      setFormData((p) => ({
        ...p,
        location: newLocation || p.location,
        event_city: venue.city || '',
        event_state: venue.state || '',
      }));
      setShowVenuePicker(false);
      setVenueSearch('');
    } catch { /* silent */ }
  }
  async function handleRemoveVenue() {
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`, { venue_id: null, location: '' }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setLinkedVenue(null);
      setFormData((p) => ({ ...p, location: '' }));
    } catch { /* silent */ }
  }

  // ─── show manager handlers ───
  async function handleSelectShowManager(profile) {
    try {
      const smRes = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { show_manager_id: profile._id }, headers);
      setLocalEvent(smRes.data);
      onUpdate(smRes.data);
      setLinkedShowManager(profile);
      setShowManagerPicker(false);
      setShowManagerSearch('');

      const profileIdStr = profile._id?.toString();
      const alreadyOnTeam = eventTeam.some((p) => p._id?.toString() === profileIdStr);
      if (alreadyOnTeam) {
        const updatedMembers = (smRes.data.team_members || []).map((m) =>
          m.personnel_profile_id?.toString() === profileIdStr ? { ...m, position: 'Show Manager' } : m
        );
        const teamRes = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
          { team_members: updatedMembers }, headers);
        setLocalEvent(teamRes.data);
        onUpdate(teamRes.data);
        setMemberPositions((p) => ({ ...p, [profileIdStr]: 'Show Manager' }));
      } else {
        const newMembers = [...(smRes.data.team_members || []),
          { personnel_profile_id: profile._id, position: 'Show Manager', added_at: new Date() }];
        const teamRes = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
          { team_members: newMembers }, headers);
        setLocalEvent(teamRes.data);
        onUpdate(teamRes.data);
        setEventTeam((p) => [...p, profile]);
        setMemberPositions((p) => ({ ...p, [profileIdStr]: 'Show Manager' }));
      }
    } catch { /* silent */ }
  }
  async function handleRemoveShowManager() {
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { show_manager_id: null }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      if (linkedShowManager) {
        const idStr = linkedShowManager._id?.toString();
        const updatedMembers = (res.data.team_members || []).map((m) =>
          m.personnel_profile_id?.toString() === idStr ? { ...m, position: '' } : m
        );
        const teamRes = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
          { team_members: updatedMembers }, headers);
        setLocalEvent(teamRes.data);
        onUpdate(teamRes.data);
        setMemberPositions((p) => ({ ...p, [idStr]: '' }));
      }
      setLinkedShowManager(null);
    } catch { /* silent */ }
  }

  // ─── team handlers ───
  async function handleAddMember(profile) {
    const newEntry = { personnel_profile_id: profile._id, position: '', added_at: new Date() };
    const updatedMembers = [...(localEvent.team_members || []), newEntry];
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { team_members: updatedMembers }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setEventTeam((p) => [...p, profile]);
      setMemberPositions((p) => ({ ...p, [profile._id.toString()]: '' }));
      setShowMemberPicker(false);
      setMemberSearch('');
    } catch { /* silent */ }
  }
  async function handleRemoveMember(profileId) {
    const updatedMembers = (localEvent.team_members || []).filter(
      (m) => m.personnel_profile_id?.toString() !== profileId
    );
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { team_members: updatedMembers }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setEventTeam((p) => p.filter((pr) => pr._id?.toString() !== profileId));
      setMemberPositions((p) => { const n = { ...p }; delete n[profileId]; return n; });
    } catch { /* silent */ }
  }
  async function handleUpdateMemberPosition(profileId, position) {
    const updatedMembers = (localEvent.team_members || []).map((m) =>
      m.personnel_profile_id?.toString() === profileId ? { ...m, position } : m
    );
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { team_members: updatedMembers }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
    } catch { /* silent */ }
  }

  // ─── task handlers ───
  async function handleAddTask() {
    const text = newTaskText.trim();
    if (!text) return;
    setIsAddingTask(true);
    try {
      const res = await axios.post(`${API_BASE}/api/events/${localEvent._id}/tasks`, { text }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setNewTaskText('');
    } catch { /* silent */ } finally { setIsAddingTask(false); }
  }
  async function handleToggleTask(task) {
    const newComplete = !task.is_complete;
    setLocalEvent((p) => ({ ...p, tasks: p.tasks.map((t) => t._id === task._id ? { ...t, is_complete: newComplete } : t) }));
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}/tasks/${task._id}`,
        { is_complete: newComplete }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
    } catch {
      setLocalEvent((p) => ({ ...p, tasks: p.tasks.map((t) => t._id === task._id ? { ...t, is_complete: task.is_complete } : t) }));
    }
  }
  async function handleDeleteTask(taskId) {
    setLocalEvent((p) => ({ ...p, tasks: p.tasks.filter((t) => t._id !== taskId) }));
    try {
      const res = await axios.delete(`${API_BASE}/api/events/${localEvent._id}/tasks/${taskId}`, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
    } catch { /* silent */ }
  }
  async function handleAssignTask(taskId, profile) {
    setActiveTaskPicker(null);
    setTaskSearch('');
    if (profile) {
      setTaskAssignees((p) => ({ ...p, [taskId]: profile }));
    } else {
      setTaskAssignees((p) => { const n = { ...p }; delete n[taskId]; return n; });
    }
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}/tasks/${taskId}`,
        { assignee_id: profile ? profile._id : null }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
    } catch { /* silent */ }
  }

  // ─── vendor handlers ───
  async function handleAddVendor(company) {
    const newAssignment = { company_id: company._id, vendor_type: pendingVendorType, rep_id: null };
    const updatedAssignments = [...(localEvent.vendor_assignments || []), newAssignment];
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { vendor_assignments: updatedAssignments }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setEventVendors((p) => [...p, { ...newAssignment, company }]);
      setShowVendorPicker(false);
      setVendorSearch('');
      setPendingVendorType('');
    } catch { /* silent */ }
  }
  async function handleRemoveVendor(companyId) {
    const updatedAssignments = (localEvent.vendor_assignments || []).filter(
      (va) => va.company_id?.toString() !== companyId
    );
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { vendor_assignments: updatedAssignments }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setEventVendors((p) => p.filter((v) => v.company?._id?.toString() !== companyId));
    } catch { /* silent */ }
  }

  // ─── rep handlers ───
  async function fetchCompanyContacts(companyId) {
    if (companyContacts[companyId]) return;
    try {
      const res = await axios.get(`${API_BASE}/api/companies/${companyId}`, headers);
      const rawContacts = res.data.contacts || [];
      const enriched = await Promise.all(
        rawContacts.map(async (c) => {
          try {
            const pRes = await axios.get(`${API_BASE}/api/personnel/${c.personnel_profile_id}`, headers);
            return { ...c, profile: pRes.data };
          } catch {
            return { ...c, profile: null };
          }
        })
      );
      setCompanyContacts((prev) => ({ ...prev, [companyId]: enriched }));
    } catch { /* silent */ }
  }
  async function handleAssignRep(companyId, profileId) {
    const updatedAssignments = (localEvent.vendor_assignments || []).map((va) =>
      va.company_id?.toString() === companyId ? { ...va, rep_id: profileId } : va
    );
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { vendor_assignments: updatedAssignments }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setEventVendors((prev) => prev.map((v) =>
        v.company?._id?.toString() === companyId ? { ...v, rep_id: profileId } : v
      ));
      setActiveRepPicker(null);
      setRepPickerSearch('');
    } catch { /* silent */ }
  }
  async function handleRemoveRep(companyId) {
    const updatedAssignments = (localEvent.vendor_assignments || []).map((va) =>
      va.company_id?.toString() === companyId ? { ...va, rep_id: null } : va
    );
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { vendor_assignments: updatedAssignments }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setEventVendors((prev) => prev.map((v) =>
        v.company?._id?.toString() === companyId ? { ...v, rep_id: null } : v
      ));
    } catch { /* silent */ }
  }

  // pre-fetch contacts for all assigned vendors (needed to show rep names)
  useEffect(() => {
    eventVendors.forEach((v) => {
      if (v.company?._id) fetchCompanyContacts(v.company._id.toString());
    });
  }, [eventVendors.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── direction handlers ───
  async function handleSaveDirection() {
    if (!newDirectionTitle.trim()) return;
    setIsSavingDirection(true);
    const updated = [...(localEvent.directions || []),
      { title: newDirectionTitle.trim(), content: newDirectionContent.trim() }];
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { directions: updated }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setNewDirectionTitle('');
      setNewDirectionContent('');
      setShowAddDirectionForm(false);
    } catch { /* silent */ } finally { setIsSavingDirection(false); }
  }
  async function handleDeleteDirection(idx) {
    const updated = (localEvent.directions || []).filter((_, i) => i !== idx);
    try {
      const res = await axios.put(`${API_BASE}/api/events/${localEvent._id}`,
        { directions: updated }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
    } catch { /* silent */ }
  }

  // ─── memory bank handlers ───
  async function handleAddMemory() {
    const text = memoryBankText.trim();
    if (!text) return;
    setIsAddingMemory(true);
    const authorName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : '';
    try {
      const res = await axios.post(`${API_BASE}/api/events/${localEvent._id}/memory`,
        { text, author_name: authorName }, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
      setMemoryBankText('');
    } catch { /* silent */ } finally { setIsAddingMemory(false); }
  }
  async function handleDeleteMemory(entryId) {
    setLocalEvent((p) => ({ ...p, memory_bank: p.memory_bank.filter((e) => e._id !== entryId) }));
    try {
      const res = await axios.delete(`${API_BASE}/api/events/${localEvent._id}/memory/${entryId}`, headers);
      setLocalEvent(res.data);
      onUpdate(res.data);
    } catch { /* silent */ }
  }

  // ─── STYLE CONSTANTS ───
  const LABEL_STYLE = {
    fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px',
    color: '#888888', letterSpacing: '1px', marginBottom: '6px', display: 'block',
  };
  const INPUT_STYLE = {
    background: 'transparent', border: 'none', borderBottom: '1px solid #565656',
    color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px',
    padding: '4px 0', outline: 'none', width: '100%',
  };
  const SECTION_HEADER_STYLE = {
    fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px',
    color: '#565656', letterSpacing: '2px', paddingBottom: '8px',
    borderBottom: '1px solid #333333', marginBottom: '20px', marginTop: '28px',
  };
  const SAVED_STYLE = { fontFamily: "'Roboto', sans-serif", fontSize: '11px', color: '#39FF14', marginTop: '3px' };
  const ERROR_STYLE = { fontFamily: "'Roboto', sans-serif", fontSize: '11px', color: '#BF2634', marginTop: '3px' };
  const PICKER_DROPDOWN_STYLE = {
    position: 'absolute', top: '100%', left: 0, right: 0,
    backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px',
    maxHeight: '240px', overflowY: 'auto', zIndex: 100,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  };
  const PICKER_SEARCH_STYLE = {
    width: '100%', padding: '8px 12px', background: '#1a1a1a',
    border: 'none', borderBottom: '1px solid #3a3a3a', color: '#F5F5F5',
    fontFamily: "'Roboto', sans-serif", fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  };
  const sc = STATUS_COLORS[localEvent.status] || STATUS_COLORS['Proposal'];

  // ─── TAB RENDERERS ───

  function renderOverview() {
    const filteredVenues = availableVenues.filter((v) =>
      [v.name, v.city, v.state].filter(Boolean).join(' ').toLowerCase().includes(venueSearch.toLowerCase())
    );
    const filteredSM = availablePersonnel.filter((p) =>
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(showManagerSearch.toLowerCase())
    );

    return (
      <div>
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px' }}>EVENT INFO</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
          {/* Title */}
          <div>
            <label style={LABEL_STYLE}>EVENT NAME</label>
            <input value={formData.title} onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
              onBlur={() => handleBlur('title')} style={INPUT_STYLE} />
            {savedFields.title && <div style={SAVED_STYLE}>Saved</div>}
            {errorFields.title && <div style={ERROR_STYLE}>Error</div>}
          </div>
          {/* Status */}
          <div>
            <label style={LABEL_STYLE}>STATUS</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <select value={formData.status} onChange={(e) => handleStatusChange(e.target.value)}
                style={{
                  ...INPUT_STYLE, width: 'auto', cursor: 'pointer',
                  backgroundColor: sc.bg, color: sc.color,
                  border: `1px solid ${sc.color}`, borderRadius: '10px',
                  padding: '3px 10px', fontWeight: 500, fontSize: '12px',
                }}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {savedFields.status && <span style={SAVED_STYLE}>Saved</span>}
              {errorFields.status && <span style={ERROR_STYLE}>Error</span>}
            </div>
          </div>
          {/* Start Date */}
          <div>
            <label style={LABEL_STYLE}>START DATE</label>
            <input type="date" value={formData.start_date}
              onChange={(e) => setFormData((p) => ({ ...p, start_date: e.target.value }))}
              onBlur={() => handleBlur('start_date')} style={{ ...INPUT_STYLE, colorScheme: 'dark' }} />
            {savedFields.start_date && <div style={SAVED_STYLE}>Saved</div>}
          </div>
          {/* End Date */}
          <div>
            <label style={LABEL_STYLE}>END DATE</label>
            <input type="date" value={formData.end_date}
              onChange={(e) => setFormData((p) => ({ ...p, end_date: e.target.value }))}
              onBlur={() => handleBlur('end_date')} style={{ ...INPUT_STYLE, colorScheme: 'dark' }} />
            {savedFields.end_date && <div style={SAVED_STYLE}>Saved</div>}
          </div>
        </div>

        {/* LOCATION */}
        <div style={SECTION_HEADER_STYLE}>LOCATION</div>
        {linkedVenue ? (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '11px', color: '#565656', fontStyle: 'italic', marginBottom: '6px' }}>
              Location auto-populated from venue
            </div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5' }}>
              {[formData.event_city, formData.event_state].filter(Boolean).join(', ') || '—'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
            <div>
              <label style={LABEL_STYLE}>CITY</label>
              <input value={formData.event_city}
                onChange={(e) => setFormData((p) => ({ ...p, event_city: e.target.value }))}
                onBlur={() => handleBlur('event_city')} placeholder="City" style={INPUT_STYLE} />
              {savedFields.event_city && <div style={SAVED_STYLE}>Saved</div>}
            </div>
            <div>
              <label style={LABEL_STYLE}>STATE</label>
              <select value={formData.event_state}
                onChange={(e) => setFormData((p) => ({ ...p, event_state: e.target.value }))}
                onBlur={() => handleBlur('event_state')}
                style={{ ...INPUT_STYLE, cursor: 'pointer', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', padding: '6px 8px' }}>
                <option value="">Select state...</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {savedFields.event_state && <div style={SAVED_STYLE}>Saved</div>}
            </div>
          </div>
        )}

        {/* TICKETS */}
        <div style={SECTION_HEADER_STYLE}>TICKETS</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
          <div>
            <label style={LABEL_STYLE}>TICKETS SOLD</label>
            <input type="number" value={formData.tickets_sold}
              onChange={(e) => setFormData((p) => ({ ...p, tickets_sold: e.target.value }))}
              onBlur={() => handleBlur('tickets_sold')} style={INPUT_STYLE} />
            {savedFields.tickets_sold && <div style={SAVED_STYLE}>Saved</div>}
          </div>
          <div>
            <label style={LABEL_STYLE}>TICKETS ON SALE</label>
            <input type="number" value={formData.tickets_on_sale}
              onChange={(e) => setFormData((p) => ({ ...p, tickets_on_sale: e.target.value }))}
              onBlur={() => handleBlur('tickets_on_sale')} style={INPUT_STYLE} />
            {savedFields.tickets_on_sale && <div style={SAVED_STYLE}>Saved</div>}
          </div>
        </div>
        {linkedVenue && fullVenueRecord?.capacity?.total && (
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '11px', color: '#565656', fontStyle: 'italic', marginTop: '-12px', marginBottom: '20px' }}>
            Venue capacity: {fullVenueRecord.capacity.total.toLocaleString()}
          </div>
        )}

        {/* VENUE */}
        <div style={SECTION_HEADER_STYLE}>VENUE</div>
        <div style={{ marginBottom: '20px', position: 'relative' }} ref={venuePickerRef}>
          {linkedVenue ? (
            <div style={{ background: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '15px', color: '#F5F5F5' }}>{linkedVenue.name}</div>
                {(linkedVenue.city || linkedVenue.state) && (
                  <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#888888', marginTop: '2px' }}>
                    {[linkedVenue.city, linkedVenue.state].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setShowVenuePicker(true)} style={{ background: 'transparent', border: '1px solid #565656', color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '12px', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>Change</button>
                <button onClick={handleRemoveVenue} style={{ background: 'none', border: 'none', color: '#BF2634', fontFamily: "'Roboto', sans-serif", fontSize: '12px', cursor: 'pointer', padding: '4px 6px' }}>Remove</button>
              </div>
            </div>
          ) : (
            <LinkButton label="+ Link Venue" onClick={() => setShowVenuePicker(true)} />
          )}
          {showVenuePicker && (
            <div style={PICKER_DROPDOWN_STYLE}>
              <input autoFocus value={venueSearch} onChange={(e) => setVenueSearch(e.target.value)}
                placeholder="Search venues..." style={PICKER_SEARCH_STYLE} />
              {filteredVenues.length === 0
                ? <div style={{ padding: '16px', fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>No venues found</div>
                : filteredVenues.map((v) => (
                  <PickerOption key={v._id} primary={v.name}
                    secondary={[v.city, v.state].filter(Boolean).join(', ')}
                    onClick={() => handleSelectVenue(v)} />
                ))}
            </div>
          )}
        </div>

        {/* SHOW MANAGER */}
        <div style={SECTION_HEADER_STYLE}>SHOW MANAGER</div>
        <div style={{ marginBottom: '20px', position: 'relative' }} ref={showManagerPickerRef}>
          {linkedShowManager ? (
            <div style={{ background: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', minWidth: '36px', borderRadius: '50%', backgroundColor: '#565656', border: '2px solid #39FF14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '14px', color: '#F5F5F5' }}>
                {linkedShowManager.first_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', color: '#F5F5F5' }}>{linkedShowManager.first_name} {linkedShowManager.last_name}</div>
                {linkedShowManager.title && <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#888888' }}>{linkedShowManager.title}</div>}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setShowManagerPicker(true)} style={{ background: 'transparent', border: '1px solid #565656', color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '12px', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>Change</button>
                <button onClick={handleRemoveShowManager} style={{ background: 'none', border: 'none', color: '#BF2634', fontFamily: "'Roboto', sans-serif", fontSize: '12px', cursor: 'pointer', padding: '4px 6px' }}>Remove</button>
              </div>
            </div>
          ) : (
            <LinkButton label="+ Assign Show Manager" onClick={() => setShowManagerPicker(true)} />
          )}
          {showManagerPicker && (
            <div style={PICKER_DROPDOWN_STYLE}>
              <input autoFocus value={showManagerSearch} onChange={(e) => setShowManagerSearch(e.target.value)}
                placeholder="Search personnel..." style={PICKER_SEARCH_STYLE} />
              {filteredSM.map((p) => <PersonnelPickerOption key={p._id} profile={p} onClick={() => handleSelectShowManager(p)} />)}
            </div>
          )}
        </div>

        {/* NOTES */}
        <div style={SECTION_HEADER_STYLE}>NOTES</div>
        <textarea value={formData.notes} onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
          onBlur={() => handleBlur('notes')} placeholder="Add notes..."
          style={{ width: '100%', backgroundColor: '#1e1e1e', border: '1px solid #333333', borderRadius: '4px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '12px', outline: 'none', resize: 'vertical', minHeight: '100px', boxSizing: 'border-box', lineHeight: '1.5' }} />
        {savedFields.notes && <div style={SAVED_STYLE}>Saved</div>}
      </div>
    );
  }

  function renderTeam() {
    const teamMemberIds = new Set((localEvent.team_members || []).map((m) => m.personnel_profile_id?.toString()));
    const filteredPersonnel = availablePersonnel.filter(
      (p) => !teamMemberIds.has(p._id?.toString()) &&
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(memberSearch.toLowerCase())
    );

    return (
      <div>
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>TEAM MEMBERS</span>
          <button onClick={() => setShowMemberPicker(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px solid #39FF14', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', marginBottom: '8px' }}>
            <AddOutlined style={{ fontSize: 14 }} />Member
          </button>
        </div>
        <div style={{ position: 'relative', marginBottom: '16px' }} ref={memberPickerRef}>
          {showMemberPicker && (
            <div style={{ ...PICKER_DROPDOWN_STYLE, maxHeight: '300px' }}>
              <input autoFocus value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search personnel..." style={PICKER_SEARCH_STYLE} />
              {filteredPersonnel.length === 0
                ? <div style={{ padding: '16px', fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>No personnel found</div>
                : filteredPersonnel.map((p) => <PersonnelPickerOption key={p._id} profile={p} onClick={() => handleAddMember(p)} />)}
            </div>
          )}
        </div>
        {isLoadingTeam
          ? <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '14px' }}>Loading team...</div>
          : eventTeam.length === 0
          ? <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656' }}>No team members added yet</div>
          : eventTeam.map((profile) => {
              const profileId = profile._id?.toString();
              const position = memberPositions[profileId] ?? '';
              return (
                <TeamMemberCard key={profileId} profile={profile} position={position}
                  onPositionChange={(val) => setMemberPositions((p) => ({ ...p, [profileId]: val }))}
                  onPositionBlur={(val) => handleUpdateMemberPosition(profileId, val)}
                  onRemove={() => handleRemoveMember(profileId)} />
              );
            })}
      </div>
    );
  }

  function renderTasksAndNotes() {
    const incomplete = localEvent.tasks.filter((t) => !t.is_complete);
    const complete = localEvent.tasks.filter((t) => t.is_complete);
    const ordered = [...incomplete, ...complete];

    return (
      <div>
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px' }}>TASKS</div>
        {ordered.length === 0 && <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', marginBottom: '16px' }}>No tasks yet</div>}

        {ordered.map((task) => {
          const assignee = taskAssignees[task._id] || null;
          const filteredTaskPersonnel = eventTeam.filter((p) =>
            `${p.first_name} ${p.last_name}`.toLowerCase().includes(taskSearch.toLowerCase())
          );
          return (
            <div key={task._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #2a2a2a' }}>
              <TaskCheckbox checked={task.is_complete} onChange={() => handleToggleTask(task)} />
              <span style={{ flex: 1, fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: task.is_complete ? '#565656' : '#F5F5F5', textDecoration: task.is_complete ? 'line-through' : 'none' }}>
                {task.text}
              </span>
              <div style={{ position: 'relative' }} ref={activeTaskPicker === task._id ? taskPickerRef : null}>
                <div onClick={() => { setActiveTaskPicker(activeTaskPicker === task._id ? null : task._id); setTaskSearch(''); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  {assignee ? (
                    <div title={`${assignee.first_name} ${assignee.last_name}`} style={{ width: '28px', height: '28px', minWidth: '28px', borderRadius: '50%', backgroundColor: '#565656', border: '2px solid #39FF14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '11px', color: '#F5F5F5' }}>
                      {assignee.first_name?.[0]?.toUpperCase() || '?'}
                    </div>
                  ) : (
                    <PersonAddOutlined style={{ fontSize: 18, color: '#565656' }} />
                  )}
                </div>
                {activeTaskPicker === task._id && (
                  <div style={{ ...PICKER_DROPDOWN_STYLE, right: 0, left: 'auto', width: '220px' }}>
                    <input autoFocus value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)} placeholder="Search team..." style={PICKER_SEARCH_STYLE} />
                    {assignee && (
                      <div onClick={() => handleAssignTask(task._id, null)} style={{ padding: '10px 16px', cursor: 'pointer', fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#BF2634', borderBottom: '1px solid #3a3a3a' }}>
                        Remove assignee
                      </div>
                    )}
                    {filteredTaskPersonnel.length === 0
                      ? <div style={{ padding: '16px', fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>No team members</div>
                      : filteredTaskPersonnel.map((p) => <PersonnelPickerOption key={p._id} profile={p} onClick={() => handleAssignTask(task._id, p)} />)}
                  </div>
                )}
              </div>
              <button onClick={() => handleDeleteTask(task._id)} style={{ background: 'transparent', border: 'none', color: '#565656', cursor: 'pointer', fontSize: '16px', padding: '0 4px', lineHeight: 1 }}>×</button>
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: '10px', marginTop: '14px', alignItems: 'center' }}>
          <input value={newTaskText} onChange={(e) => setNewTaskText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddTask(); }}
            placeholder="Add a task..."
            style={{ flex: 1, background: 'transparent', border: 'none', borderBottom: '1px solid #565656', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '6px 0', outline: 'none' }} />
          <button onClick={handleAddTask} disabled={isAddingTask || !newTaskText.trim()}
            style={{ backgroundColor: 'transparent', border: '1px solid #39FF14', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', padding: '5px 14px', borderRadius: '4px', cursor: isAddingTask || !newTaskText.trim() ? 'default' : 'pointer', opacity: isAddingTask || !newTaskText.trim() ? 0.5 : 1, whiteSpace: 'nowrap' }}>
            Add
          </button>
        </div>

        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '40px' }}>NOTES</div>
        <textarea value={formData.notes} onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
          onBlur={() => handleBlur('notes')} placeholder="Add notes..."
          style={{ width: '100%', backgroundColor: '#1e1e1e', border: '1px solid #333333', borderRadius: '4px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '12px', outline: 'none', resize: 'vertical', minHeight: '160px', boxSizing: 'border-box', lineHeight: '1.5' }} />
        {savedFields.notes && <div style={SAVED_STYLE}>Saved</div>}
      </div>
    );
  }

  function renderVendors() {
    const filteredVendors = availableVendors.filter((v) =>
      v.name?.toLowerCase().includes(vendorSearch.toLowerCase())
    );
    const assignedIds = new Set((localEvent.vendor_assignments || []).map((va) => va.company_id?.toString()));

    return (
      <div>
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>EVENT VENDORS</span>
          <button onClick={() => setShowVendorPicker(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px solid #39FF14', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', marginBottom: '8px' }}>
            <AddOutlined style={{ fontSize: 14 }} />Vendor
          </button>
        </div>

        {/* Vendor picker */}
        <div style={{ position: 'relative', marginBottom: '16px' }} ref={vendorPickerRef}>
          {showVendorPicker && (
            <div style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '16px', marginBottom: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
              <div style={{ marginBottom: '12px' }}>
                <label style={LABEL_STYLE}>VENDOR TYPE</label>
                <select value={pendingVendorType} onChange={(e) => setPendingVendorType(e.target.value)}
                  style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '8px 10px', outline: 'none', boxSizing: 'border-box' }}>
                  <option value="">Select type...</option>
                  {VENDOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ position: 'relative' }}>
                <SearchOutlined style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#565656', pointerEvents: 'none' }} />
                <input value={vendorSearch} onChange={(e) => setVendorSearch(e.target.value)} autoFocus
                  placeholder="Search vendors..." style={{ ...PICKER_SEARCH_STYLE, border: '1px solid #3a3a3a', borderRadius: '4px', paddingLeft: '30px' }} />
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '4px' }}>
                {filteredVendors.filter((v) => !assignedIds.has(v._id?.toString())).length === 0
                  ? <div style={{ padding: '12px 0', fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>No vendors found</div>
                  : filteredVendors.filter((v) => !assignedIds.has(v._id?.toString())).map((v) => (
                    <PickerOption key={v._id} primary={v.name}
                      secondary={[v.city, v.state].filter(Boolean).join(', ')}
                      onClick={() => handleAddVendor(v)} />
                  ))}
              </div>
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowVendorPicker(false); setVendorSearch(''); setPendingVendorType(''); }}
                  style={{ background: 'transparent', border: '1px solid #565656', color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '12px', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {eventVendors.length === 0
          ? <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656' }}>No vendors assigned yet</div>
          : eventVendors.map((vendor, idx) => (
            <div key={idx} style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '16px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', letterSpacing: '1px', textTransform: 'uppercase' }}>{vendor.vendor_type || '—'}</span>
                <VendorRemoveButton onRemove={() => handleRemoveVendor(vendor.company?._id?.toString())} />
              </div>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '16px', color: '#F5F5F5', marginBottom: '2px' }}>{vendor.company?.name || '—'}</div>
              {(vendor.company?.city || vendor.company?.state) && (
                <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#888888', marginBottom: '8px' }}>
                  {[vendor.company.city, vendor.company.state].filter(Boolean).join(', ')}
                </div>
              )}
              {/* Rep section */}
              <div>
                {vendor.rep_id ? (() => {
                  const contacts = companyContacts[vendor.company?._id?.toString()] || [];
                  const repContact = contacts.find((c) => c.personnel_profile_id?.toString() === vendor.rep_id?.toString());
                  const repProfile = repContact?.profile;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1a1a1a', borderRadius: '4px', padding: '8px 12px' }}>
                      <div>
                        <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#F5F5F5', fontWeight: 500 }}>
                          {repProfile ? `${repProfile.first_name || ''} ${repProfile.last_name || ''}`.trim() || '—' : 'Loading...'}
                        </div>
                        {repContact?.contact_types?.length > 0 && (
                          <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '11px', color: '#888888', marginTop: '2px' }}>
                            {repContact.contact_types.join(', ')}
                          </div>
                        )}
                      </div>
                      <button onClick={() => handleRemoveRep(vendor.company?._id?.toString())}
                        style={{ background: 'transparent', border: 'none', color: '#888888', fontFamily: "'Roboto', sans-serif", fontSize: '12px', cursor: 'pointer', padding: '0' }}>
                        Remove
                      </button>
                    </div>
                  );
                })() : (
                  <button onClick={() => {
                    fetchCompanyContacts(vendor.company?._id?.toString());
                    setActiveRepPicker(vendor.company?._id?.toString());
                    setRepPickerSearch('');
                  }}
                    style={{ background: 'transparent', border: 'none', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontSize: '12px', cursor: 'pointer', padding: '0' }}>
                    + Assign Rep
                  </button>
                )}
                {activeRepPicker === vendor.company?._id?.toString() && (
                  <div ref={repPickerRef} style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '12px', marginTop: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <SearchOutlined style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#565656', pointerEvents: 'none' }} />
                      <input value={repPickerSearch} onChange={(e) => setRepPickerSearch(e.target.value)} autoFocus
                        placeholder="Search contacts..."
                        style={{ ...PICKER_SEARCH_STYLE, border: '1px solid #3a3a3a', borderRadius: '4px', paddingLeft: '30px' }} />
                    </div>
                    <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                      {(() => {
                        const contacts = companyContacts[vendor.company?._id?.toString()] || [];
                        if (contacts.length === 0) return (
                          <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic', padding: '8px 0' }}>
                            Loading contacts...
                          </div>
                        );
                        const filtered = contacts.filter((c) => {
                          if (!c.profile) return false;
                          const name = `${c.profile.first_name || ''} ${c.profile.last_name || ''}`.toLowerCase();
                          return name.includes(repPickerSearch.toLowerCase());
                        });
                        if (filtered.length === 0) return (
                          <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', fontStyle: 'italic', padding: '8px 0' }}>
                            No contacts found
                          </div>
                        );
                        return filtered.map((c) => (
                          <PickerOption key={c.personnel_profile_id?.toString()}
                            primary={`${c.profile.first_name || ''} ${c.profile.last_name || ''}`.trim()}
                            secondary={c.contact_types?.join(', ')}
                            onClick={() => handleAssignRep(vendor.company?._id?.toString(), c.personnel_profile_id?.toString())} />
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    );
  }

  function renderVenue() {
    const vr = fullVenueRecord;
    const VENUE_LABEL = { fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', letterSpacing: '1px', textTransform: 'uppercase', marginRight: '6px' };
    const VENUE_VALUE = { fontFamily: "'Roboto', sans-serif", fontSize: '15px', color: '#F5F5F5' };
    const TEXTAREA_STYLE = { width: '100%', backgroundColor: '#1e1e1e', border: '1px solid #333333', borderRadius: '4px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '10px 12px', outline: 'none', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box', lineHeight: '1.5' };

    if (!linkedVenue) {
      return (
        <div>
          <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px' }}>LINKED VENUE</div>
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656', fontStyle: 'italic' }}>
            Link a venue in the Overview tab first
          </div>
        </div>
      );
    }

    return (
      <div>
        {/* LINKED VENUE */}
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px' }}>LINKED VENUE</div>
        {isLoadingVenue ? (
          <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '13px', marginBottom: '24px' }}>Loading venue details...</div>
        ) : vr ? (
          <div style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '20px', marginBottom: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div>
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '20px', color: '#FFFFFF', marginBottom: '4px' }}>{vr.name}</div>
                {[vr.address1, vr.city, vr.state, vr.zip].filter(Boolean).length > 0 && (
                  <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#888888', marginBottom: '6px' }}>
                    {[vr.address1, vr.address2].filter(Boolean).join(', ')}{vr.address1 && (vr.city || vr.state) ? ', ' : ''}
                    {[vr.city, vr.state, vr.zip].filter(Boolean).join(', ')}
                  </div>
                )}
                {vr.venue_type && (
                  <span style={{ backgroundColor: 'rgba(83,145,255,0.1)', color: '#5391FF', border: '1px solid #5391FF', borderRadius: '8px', padding: '2px 8px', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '10px' }}>
                    {vr.venue_type}
                  </span>
                )}
                {vr.website && (
                  <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#39FF14', marginTop: '8px' }}>
                    <a href={vr.website} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{vr.website}</a>
                  </div>
                )}
              </div>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', color: '#39FF14', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, marginLeft: '16px' }}
                onClick={() => { sessionStorage.setItem('vance_select_venue', vr._id); navigate('/venues'); }}>
                View full venue record <ChevronRightOutlined style={{ fontSize: 16 }} />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '16px', marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '15px', color: '#F5F5F5' }}>{linkedVenue.name}</div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', color: '#39FF14', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
              onClick={() => { sessionStorage.setItem('vance_select_venue', linkedVenue._id); navigate('/venues'); }}>
              View full venue record <ChevronRightOutlined style={{ fontSize: 16 }} />
            </div>
          </div>
        )}

        {/* CAPACITY */}
        {vr && (
          <>
            <div style={SECTION_HEADER_STYLE}>CAPACITY</div>
            {(() => {
              const cap = vr.capacity || {};
              const fields = [
                { label: 'Total', value: cap.total },
                { label: 'Floor', value: cap.floor },
                { label: 'Orchestra', value: cap.orchestra },
                { label: 'Balcony', value: cap.balcony },
                { label: 'Lawn', value: cap.lawn },
                { label: 'VIP', value: cap.vip },
                { label: 'Level 100', value: cap.level_100 },
                { label: 'Level 200', value: cap.level_200 },
                { label: 'Level 300', value: cap.level_300 },
              ].filter((f) => f.value != null && f.value !== 0);
              return fields.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  {fields.map((f) => (
                    <div key={f.label}>
                      <div style={VENUE_LABEL}>{f.label}</div>
                      <div style={VENUE_VALUE}>{f.value.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', color: '#565656', fontStyle: 'italic', marginBottom: '24px' }}>No capacity data on file</div>
              );
            })()}

            {/* LOADING */}
            <div style={SECTION_HEADER_STYLE}>LOADING</div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                <span style={VENUE_LABEL}>Dock</span>
                <span style={{ ...VENUE_VALUE, fontSize: '14px', color: vr.loading?.has_dock ? '#39FF14' : '#565656' }}>
                  {vr.loading?.has_dock ? 'Available' : 'No dock'}
                </span>
                {vr.loading?.docks?.length > 0 && (
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#888888' }}>
                    · {vr.loading.docks.length} dock{vr.loading.docks.length !== 1 ? 's' : ''} on file
                  </span>
                )}
              </div>
              {vr.loading?.load_in_area_desc && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={VENUE_LABEL}>Load-In Area</div>
                  <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5', marginTop: '3px' }}>{vr.loading.load_in_area_desc}</div>
                </div>
              )}
              {vr.loading?.access_notes && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={VENUE_LABEL}>Access Notes</div>
                  <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5', marginTop: '3px', whiteSpace: 'pre-wrap' }}>{vr.loading.access_notes}</div>
                </div>
              )}
              {!vr.loading?.load_in_area_desc && !vr.loading?.access_notes && !vr.loading?.has_dock && (
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', color: '#565656', fontStyle: 'italic' }}>No loading data on file</div>
              )}
            </div>

            {/* RIGGING */}
            <div style={SECTION_HEADER_STYLE}>RIGGING</div>
            <div style={{ marginBottom: '24px' }}>
              {(() => {
                const r = vr.rigging || {};
                const fields = [
                  { label: 'Available', value: r.available ? 'Yes' : 'No' },
                  r.trim_height && { label: 'Trim Height', value: `${r.trim_height}'` },
                  r.grid_weight && { label: 'Grid Weight', value: `${r.grid_weight} lbs` },
                  r.grid_access && { label: 'Grid Access', value: r.grid_access },
                  { label: 'Diagram', value: r.diagram_available ? 'On file' : 'Not available' },
                ].filter(Boolean);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                    {fields.map((f) => (
                      <div key={f.label}>
                        <div style={VENUE_LABEL}>{f.label}</div>
                        <div style={{ ...VENUE_VALUE, fontSize: '14px', color: f.label === 'Available' && r.available ? '#39FF14' : '#F5F5F5' }}>{f.value}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {/* EVENT-SPECIFIC OVERRIDES */}
        <div style={SECTION_HEADER_STYLE}>EVENT-SPECIFIC OVERRIDES</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
          <div>
            <label style={LABEL_STYLE}>SHOW WIFI NAME</label>
            <input value={formData.vc_wifi_name} onChange={(e) => setFormData((p) => ({ ...p, vc_wifi_name: e.target.value }))}
              onBlur={() => handleBlur('vc_wifi_name')} style={INPUT_STYLE} />
            {savedFields.vc_wifi_name && <div style={SAVED_STYLE}>Saved</div>}
          </div>
          <div>
            <label style={LABEL_STYLE}>SHOW WIFI PASSWORD</label>
            <input value={formData.vc_wifi_password} onChange={(e) => setFormData((p) => ({ ...p, vc_wifi_password: e.target.value }))}
              onBlur={() => handleBlur('vc_wifi_password')} style={INPUT_STYLE} />
            {savedFields.vc_wifi_password && <div style={SAVED_STYLE}>Saved</div>}
          </div>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={LABEL_STYLE}>LOAD-IN NOTES</label>
          <textarea value={formData.vc_load_in_notes} onChange={(e) => setFormData((p) => ({ ...p, vc_load_in_notes: e.target.value }))}
            onBlur={() => handleBlur('vc_load_in_notes')} style={TEXTAREA_STYLE} />
          {savedFields.vc_load_in_notes && <div style={SAVED_STYLE}>Saved</div>}
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={LABEL_STYLE}>ACCESS NOTES</label>
          <textarea value={formData.vc_access_notes} onChange={(e) => setFormData((p) => ({ ...p, vc_access_notes: e.target.value }))}
            onBlur={() => handleBlur('vc_access_notes')} style={TEXTAREA_STYLE} />
          {savedFields.vc_access_notes && <div style={SAVED_STYLE}>Saved</div>}
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={LABEL_STYLE}>NETWORK NOTES</label>
          <textarea value={formData.vc_network_notes} onChange={(e) => setFormData((p) => ({ ...p, vc_network_notes: e.target.value }))}
            onBlur={() => handleBlur('vc_network_notes')} style={TEXTAREA_STYLE} />
          {savedFields.vc_network_notes && <div style={SAVED_STYLE}>Saved</div>}
        </div>

        {/* PARKING */}
        <div style={SECTION_HEADER_STYLE}>PARKING</div>
        <div style={{ marginBottom: '20px' }}>
          <label style={LABEL_STYLE}>PARKING NOTES</label>
          <textarea value={formData.parking_notes} onChange={(e) => setFormData((p) => ({ ...p, parking_notes: e.target.value }))}
            onBlur={() => handleBlur('parking_notes')} placeholder="Parking instructions..." style={TEXTAREA_STYLE} />
          {savedFields.parking_notes && <div style={SAVED_STYLE}>Saved</div>}
        </div>

        {/* ROOM ASSIGNMENTS */}
        <div style={SECTION_HEADER_STYLE}>ROOM ASSIGNMENTS</div>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656', fontStyle: 'italic', marginBottom: '12px' }}>No room assignments yet</div>
          <button onClick={() => console.log('add room assignment')}
            style={{ background: 'transparent', border: '1px solid #39FF14', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AddOutlined style={{ fontSize: 14 }} />Room
          </button>
        </div>
      </div>
    );
  }

  function renderDirections() {
    const directions = localEvent.directions || [];
    return (
      <div>
        <div style={{ ...SECTION_HEADER_STYLE, marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>DIRECTIONS</span>
          {!showAddDirectionForm && (
            <button onClick={() => setShowAddDirectionForm(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px solid #39FF14', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', marginBottom: '8px' }}>
              <AddOutlined style={{ fontSize: 14 }} />Direction
            </button>
          )}
        </div>

        {showAddDirectionForm && (
          <div style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={LABEL_STYLE}>DIRECTION SET TITLE</label>
              <input autoFocus value={newDirectionTitle} onChange={(e) => setNewDirectionTitle(e.target.value)}
                placeholder="e.g. Dock Directions, Hotel Directions"
                style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '8px 12px', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={LABEL_STYLE}>DIRECTIONS</label>
              <textarea value={newDirectionContent} onChange={(e) => setNewDirectionContent(e.target.value)}
                placeholder="Enter turn-by-turn directions..."
                style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '10px 12px', outline: 'none', resize: 'vertical', minHeight: '200px', boxSizing: 'border-box', lineHeight: '1.6' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowAddDirectionForm(false); setNewDirectionTitle(''); setNewDirectionContent(''); }}
                style={{ background: 'transparent', border: '1px solid #565656', color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '13px', padding: '7px 16px', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSaveDirection} disabled={isSavingDirection || !newDirectionTitle.trim()}
                style={{ background: '#BF2634', border: 'none', color: '#FFF', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', padding: '7px 20px', borderRadius: '4px', cursor: isSavingDirection || !newDirectionTitle.trim() ? 'default' : 'pointer', opacity: !newDirectionTitle.trim() ? 0.5 : 1 }}>
                {isSavingDirection ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {directions.length === 0 && !showAddDirectionForm && (
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656' }}>No direction sets yet</div>
        )}

        {directions.map((dir, idx) => (
          <div key={idx} style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '20px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '16px', color: '#FFFFFF' }}>{dir.title}</span>
              <DeleteButton onDelete={() => handleDeleteDirection(idx)} />
            </div>
            {dir.content && (
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '14px', color: '#888888', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {dir.content}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  function renderMemoryBank() {
    const entries = [...(localEvent.memory_bank || [])].reverse();
    return (
      <div>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', letterSpacing: '2px', marginBottom: '4px' }}>MEMORY BANK</div>
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '12px', color: '#3a3a3a', fontStyle: 'italic', marginBottom: '20px' }}>
            Post-event notes and reflections. Reference these when planning future events.
          </div>

          <textarea value={memoryBankText} onChange={(e) => setMemoryBankText(e.target.value)}
            placeholder="Add a note for future reference..."
            onFocus={(e) => { e.target.style.borderColor = '#39FF14'; }}
            onBlur={(e) => { e.target.style.borderColor = '#3a3a3a'; }}
            style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', padding: '12px 16px', outline: 'none', resize: 'vertical', minHeight: '100px', boxSizing: 'border-box', lineHeight: '1.5', transition: 'border-color 0.15s ease' }} />
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleAddMemory} disabled={isAddingMemory || !memoryBankText.trim()}
              onMouseEnter={() => setMemoryBankHover(true)}
              onMouseLeave={() => setMemoryBankHover(false)}
              style={{ backgroundColor: isAddingMemory ? '#888888' : (memoryBankHover ? '#8B1C26' : '#BF2634'), color: '#FFFFFF', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', padding: '8px 20px', borderRadius: '4px', border: 'none', cursor: isAddingMemory || !memoryBankText.trim() ? 'default' : 'pointer', opacity: !memoryBankText.trim() ? 0.5 : 1, transition: 'background-color 0.15s ease' }}>
              {isAddingMemory ? 'Saving...' : 'Add Note'}
            </button>
          </div>
        </div>

        {entries.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '32px', gap: '10px' }}>
            <BookmarkBorderOutlined style={{ fontSize: 48, color: '#3a3a3a' }} />
            <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656' }}>No notes yet</div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', color: '#3a3a3a', textAlign: 'center' }}>Add notes after the event to reference for future planning</div>
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry._id} style={{ borderLeft: '3px solid #E5C04B', borderRadius: '0 6px 6px 0', backgroundColor: '#2a2a2a', padding: '16px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div>
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', color: '#E5C04B' }}>{entry.author_name || 'Unknown'}</span>
                  <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '12px', color: '#565656', marginLeft: '10px' }}>{formatTimestamp(entry.created_at)}</span>
                </div>
                <DeleteButton onDelete={() => handleDeleteMemory(entry._id)} />
              </div>
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, fontSize: '14px', color: '#F5F5F5', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {entry.text}
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  function renderDocuments() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '64px', gap: '12px' }}>
        <UploadFileOutlined style={{ fontSize: 48, color: '#3a3a3a' }} />
        <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '16px', color: '#565656' }}>Document storage coming soon</div>
        <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', color: '#3a3a3a', textAlign: 'center', maxWidth: '280px' }}>
          Production documents, contracts, and files will be stored here
        </div>
      </div>
    );
  }

  function renderActivity() {
    const log = [...(localEvent.activity_log || [])].reverse();
    if (log.length === 0) {
      return <div style={{ color: '#565656', fontFamily: "'Roboto', sans-serif", fontSize: '14px', marginTop: '24px' }}>No activity yet</div>;
    }
    return (
      <div style={{ marginTop: '4px' }}>
        {log.map((entry, i) => (
          <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #2a2a2a', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5', flex: 1 }}>{entry.detail}</span>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#565656', whiteSpace: 'nowrap' }}>{new Date(entry.timestamp).toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  }

  // ─── RENDER ───
  return (
    <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#2a2a2a', borderBottom: '1px solid #3a3a3a', padding: '24px 48px 0 48px', flexShrink: 0 }}>

        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          {localEvent.project_id ? (
            <BreadcrumbProjectLink
              label={linkedProject?.title || 'Project'}
              onClick={() => { sessionStorage.setItem('vance_select_project', localEvent.project_id); navigate('/projects'); }}
            />
          ) : (
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656' }}>No Project</span>
          )}
          <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656' }}>→</span>
          <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#F5F5F5' }}>{localEvent.title}</span>
        </div>

        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div>
            <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 900, fontSize: '32px', color: '#FFFFFF', lineHeight: 1.15 }}>
              {localEvent.title}
            </div>
            {localEvent.location && (
              <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '16px', color: '#888888', marginTop: '4px' }}>
                {localEvent.location}
              </div>
            )}
          </div>
          <span style={{ backgroundColor: sc.bg, color: sc.color, border: `1px solid ${sc.color}`, borderRadius: '12px', padding: '6px 16px', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', flexShrink: 0, marginLeft: '24px', marginTop: '4px' }}>
            {localEvent.status || 'Proposal'}
          </span>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', paddingBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', textTransform: 'uppercase' }}>Dates</span>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5' }}>
              {formatDateRange(localEvent.start_date, localEvent.end_date) || '—'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', textTransform: 'uppercase' }}>Show Manager</span>
            {linkedShowManager ? (
              <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5' }}>
                {linkedShowManager.first_name} {linkedShowManager.last_name}
              </span>
            ) : (
              <span onClick={() => { setActiveTab(0); setTimeout(() => setShowManagerPicker(true), 50); }}
                style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#39FF14', cursor: 'pointer' }}>
                + Assign
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', textTransform: 'uppercase' }}>Venue</span>
            {linkedVenue ? (
              <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5' }}>
                {linkedVenue.name}
              </span>
            ) : (
              <span onClick={() => { setActiveTab(0); setTimeout(() => setShowVenuePicker(true), 50); }}
                style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#39FF14', cursor: 'pointer' }}>
                + Link
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', textTransform: 'uppercase' }}>Tickets</span>
            <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5' }}>
              {localEvent.tickets_sold != null ? localEvent.tickets_sold : '—'} sold / {localEvent.tickets_on_sale != null ? localEvent.tickets_on_sale : '—'} on sale
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', alignItems: 'flex-end', borderBottom: '1px solid #3a3a3a', width: '100%' }}>
          <div style={{ display: 'flex', flex: 1 }}>
            {TABS.slice(0, -1).map((tab, i) => (
              <div key={tab} onClick={() => setActiveTab(i)}
                style={{ padding: '10px 16px', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', color: activeTab === i ? '#39FF14' : '#888888', borderBottom: activeTab === i ? '2px solid #39FF14' : '2px solid transparent', cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap' }}>
                {tab}
              </div>
            ))}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            {(() => {
              const i = TABS.length - 1;
              return (
                <div onClick={() => setActiveTab(i)}
                  style={{ padding: '10px 16px', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', color: activeTab === i ? '#39FF14' : '#888888', borderBottom: activeTab === i ? '2px solid #39FF14' : '2px solid transparent', cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap' }}>
                  {TABS[i]}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 48px', backgroundColor: '#222222' }}>
        {activeTab === 0 && renderOverview()}
        {activeTab === 1 && renderTeam()}
        {activeTab === 2 && renderTasksAndNotes()}
        {activeTab === 3 && renderVendors()}
        {activeTab === 4 && renderVenue()}
        {activeTab === 5 && renderDirections()}
        {activeTab === 6 && renderMemoryBank()}
        {activeTab === 7 && renderDocuments()}
        {activeTab === 8 && renderActivity()}
      </div>
    </div>
  );
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────

function LinkButton({ label, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: hovered ? 'rgba(57,255,20,0.08)' : 'transparent', border: '1px solid #39FF14', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '13px', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s ease' }}>
      <AddOutlined style={{ fontSize: 16 }} />{label}
    </button>
  );
}

function PickerOption({ primary, secondary, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ padding: '10px 16px', cursor: 'pointer', backgroundColor: hovered ? 'rgba(57,255,20,0.06)' : 'transparent', transition: 'background 0.1s ease' }}>
      <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', color: hovered ? '#39FF14' : '#F5F5F5' }}>{primary}</div>
      {secondary && <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#888888', marginTop: '2px' }}>{secondary}</div>}
    </div>
  );
}

function PersonnelPickerOption({ profile, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', cursor: 'pointer', backgroundColor: hovered ? 'rgba(57,255,20,0.06)' : 'transparent', transition: 'background 0.1s ease' }}>
      <div style={{ width: '32px', height: '32px', minWidth: '32px', borderRadius: '50%', backgroundColor: '#565656', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '13px', color: '#F5F5F5' }}>
        {profile.first_name?.[0]?.toUpperCase() || '?'}
      </div>
      <div>
        <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', color: hovered ? '#39FF14' : '#F5F5F5' }}>{profile.first_name} {profile.last_name}</div>
        {profile.cell_phone && <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#888888' }}>{profile.cell_phone}</div>}
      </div>
    </div>
  );
}

function TeamMemberCard({ profile, position, onPositionChange, onPositionBlur, onRemove }) {
  const [removeHovered, setRemoveHovered] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ width: '40px', height: '40px', minWidth: '40px', borderRadius: '50%', backgroundColor: '#565656', border: '2px solid #39FF14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '16px', color: '#F5F5F5' }}>
        {profile.first_name?.[0]?.toUpperCase() || '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '15px', color: '#F5F5F5', marginBottom: '4px' }}>{profile.first_name} {profile.last_name}</div>
        <input value={position} onChange={(e) => onPositionChange(e.target.value)} onBlur={(e) => onPositionBlur(e.target.value)}
          placeholder="Add position..."
          style={{ background: 'transparent', border: 'none', borderBottom: '1px solid #333333', color: '#888888', fontFamily: "'Roboto', sans-serif", fontSize: '13px', padding: '2px 0', outline: 'none', width: '100%', maxWidth: '240px' }} />
        <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '10px', color: '#3a3a3a', fontStyle: 'italic', marginTop: '3px' }}>
          Position options customizable in Admin Panel
        </div>
        {profile.cell_phone && <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', marginTop: '4px' }}>{profile.cell_phone}</div>}
      </div>
      <button onClick={onRemove} onMouseEnter={() => setRemoveHovered(true)} onMouseLeave={() => setRemoveHovered(false)}
        style={{ background: 'none', border: 'none', color: removeHovered ? '#BF2634' : '#565656', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.15s ease' }}>
        <PersonRemoveOutlined style={{ fontSize: 20, color: 'inherit' }} />
      </button>
    </div>
  );
}

function DeleteButton({ onDelete }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onDelete} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: 'none', border: 'none', color: hovered ? '#BF2634' : '#565656', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.15s ease' }}>
      <DeleteOutlined style={{ fontSize: 18, color: 'inherit' }} />
    </button>
  );
}

function VendorRemoveButton({ onRemove }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onRemove} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: 'none', border: 'none', color: hovered ? '#BF2634' : '#565656', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', transition: 'color 0.15s ease' }}>
      <PersonRemoveOutlined style={{ fontSize: 18, color: 'inherit' }} />
    </button>
  );
}

function BreadcrumbProjectLink({ label, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontFamily: "'Roboto', sans-serif",
        fontSize: '13px',
        color: '#39FF14',
        cursor: 'pointer',
        textDecoration: hovered ? 'underline' : 'none',
        transition: 'text-decoration 0.1s ease',
      }}
    >
      {label}
    </span>
  );
}
